import axios from '@/zenky'
import storage from '@/storage'
import config from '@/config'
import get from 'lodash/get'
import { getCityContacts, getFirstCityContact } from '@/helpers/contacts'
import cachedState from '@/cachedState'

export default {
  namespaced: true,

  state: {
    store: {},
    cities: [],
    activeCityId: '',
    broadcastMessage: null,
    currency: null,
  },

  getters: {
    store(state) {
      return state.store
    },

    cities(state) {
      return state.cities
    },

    hasMultipleCities(state) {
      return state.cities && state.cities.length > 1
    },

    activeCityId(state) {
      return state.activeCityId
    },

    activeCity(state) {
      return state.cities.find(c => c.id === state.activeCityId)
    },

    activeCityStocks(state, getters) {
      if (!getters.activeCity || !getters.activeCity.stocks || !getters.activeCity.stocks.length) {
        return []
      }

      return getters.activeCity.stocks
    },

    visibleActiveCityStocks(state, getters) {
      if (!getters.activeCityStocks.length) {
        return []
      }

      return getters.activeCityStocks.filter(stock => !stock.hidden)
    },

    activeCityPhone(state, getters) {
      if (!getters.activeCity) {
        return {}
      }

      return getFirstCityContact(getters.activeCity, 'phone')
    },

    activeCityEmail(state, getters) {
      if (!getters.activeCity) {
        return {}
      }

      return getFirstCityContact(getters.activeCity, 'email')
    },

    activeCitySocials(state, getters) {
      if (!getters.activeCity) {
        return []
      }

      return getCityContacts(
        getters.activeCity,
        ['vk', 'facebook', 'instagram', 'twitter', 'telegram'],
        getters.store?.contacts || [],
      )
    },

    authenticationSmsEnabled(state) {
      if (!state.store) {
        return false
      } else if (!state.store.settings) {
        return false
      } else if (!state.store.settings.authentication) {
        return false
      }

      return state.store.settings.authentication.sms_authentication_enabled === true
    },

    authenticationEnabled(state, getters, _, rootGetters) {
      if (getters.authenticationSmsEnabled) {
        return true;
      }

      return rootGetters['settings/authentication']?.enable_without_sms === true;
    },

    checkoutUIEnabled(state) {
      if (!state.store) {
        return false;
      } else if (!state.store.settings) {
        return false;
      } else if (!state.store.settings.orders) {
        return false;
      } else if (!state.store.settings.orders.checkout_ui) {
        return false;
      }

      return state.store.settings.orders.checkout_ui.enabled === true;
    },

    productsSearchEnabled(state) {
      return state.store?.settings?.products?.search?.enabled === true;
    },

    orderConfirmationRequired(state) {
      if (!state.store) {
        return false
      } else if (!state.store.settings) {
        return false
      } else if (!state.store.settings.orders) {
        return false
      }

      return state.store.settings.orders.confirmation_required === true
    },

    orderAuthenticationMethod(state) {
      if (!state.store) {
        return 'disabled'
      } else if (!state.store.settings) {
        return 'disabled'
      } else if (!state.store.settings.orders) {
        return 'disabled'
      }

      return state.store.settings.orders.authentication_method.id
    },

    orderPaymentMethods(state) {
      if (!state.store) {
        return []
      } else if (!state.store.settings) {
        return []
      } else if (!state.store.settings.orders) {
        return []
      } else if (!Array.isArray(state.store.settings.orders.payment_methods)) {
        return []
      }

      return state.store.settings.orders.payment_methods
    },

    orderDeliveryMethods(state) {
      if (!state.store) {
        return ['pickup', 'delivery']
      } else if (!state.store.settings) {
        return ['pickup', 'delivery']
      } else if (!state.store.settings.orders) {
        return ['pickup', 'delivery']
      } else if (!Array.isArray(state.store.settings.orders.delivery_methods)) {
        return ['pickup', 'delivery']
      }

      return state.store.settings.orders.delivery_methods.map(method => method.id)
    },

    orderOptions(state) {
      if (!state.store) {
        return ['persons_count', 'delivery_time']
      } else if (!state.store.settings) {
        return ['persons_count', 'delivery_time']
      } else if (!state.store.settings.orders) {
        return ['persons_count', 'delivery_time']
      } else if (!Array.isArray(state.store.settings.orders.options)) {
        return ['persons_count', 'delivery_time']
      }

      return state.store.settings.orders.options.map(option => option.id)
    },

    orderRepeatsEnabled(state) {
      return state.store?.settings?.orders?.repeats?.enabled === true;
    },

    minOrderPrice(state) {
      if (!state.store) {
        return null
      } else if (!state.store.settings) {
        return null
      } else if (!state.store.settings.orders) {
        return null
      } else if (typeof state.store.settings.orders.min_price === 'undefined') {
        return null
      } else if (state.store.settings.orders.min_price === null) {
        return null
      }

      return state.store.settings.orders.min_price
    },

    supportsOnlineCardPayments(state, getters) {
      return getters.orderPaymentMethods.some(m => m.id === 'cloudpayments');
    },

    hasNativeApps(state) {
      const apps = state.store?.settings?.applications;

      if (!apps) {
        return false
      }

      return !!apps?.app_store?.url || !!apps?.google_play?.url
    },

    broadcastMessage(state) {
      return state.broadcastMessage
    },

    hasBroadcastMessage(state) {
      if (!state.broadcastMessage) {
        return false
      }

      return state.broadcastMessage.message !== null && state.broadcastMessage.message.toString().length > 0
    },

    hasCurrency(state) {
      return state.currency !== null && typeof state.currency.symbol !== 'undefined' && state.currency.symbol
    },

    currency(state) {
      return state.currency
    },

    countryCode(state) {
      return get(state.store, 'settings.locale.country.code', 'RU')
    },

    loyaltyEnabled(state) {
      return get(state.store, 'settings.loyalty.enabled', false) === true
    },

    shouldDisplayBonusesPin(state) {
      return get(state.store, 'settings.loyalty.display_bonuses_pin', false) === true
    },

    balanceAdjustmentEnabled(state) {
      return get(state.store, 'settings.loyalty.bonuses.adjustment_enabled', false) === true
    },

    shouldDisplayBonusesOnDemand(state) {
      return get(state.store, 'settings.loyalty.bonuses.display_on_demand', false) === true
    },

    addressesResolver(state) {
      return get(state.store, 'settings.addresses.resolver.id', 'dadata')
    },
  },

  mutations: {
    setStore(state, store) {
      state.store = store
    },

    setCities(state, cities) {
      state.cities = cities
    },

    setActiveCity(state, activeCityId) {
      state.activeCityId = activeCityId
    },

    setBroadcastMessage(state, message) {
      state.broadcastMessage = message
    },

    setCurrency(state, currency) {
      state.currency = currency
    },
  },

  actions: {
    loadStore({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const savedCityId = storage.activeCityId()
        let includes = config.includes.store.without_cities

        const storeHandler = store => {
          commit('setStore', store)
          dispatch('saveStoreToStorage', store)
          dispatch('loadBroadcastMessage')

          if (savedCityId) {
            commit('setCities', store.cities)
            dispatch('saveActiveCity', savedCityId)
          }

          commit('setCurrency', get(store, 'settings.currency.data'))
        }

        const storeLoader = () => {
          const store = cachedState.store()

          if (store !== null) {
            storeHandler(store)

            return resolve(store)
          }

          axios.get(`store?with=${includes}`)
            .then(({ data }) => {
              storeHandler(data.data)

              resolve(data.data)
            })
            .catch(err => reject(err))
        }

        if (!savedCityId) {
          dispatch('loadCities')
            .then(() => storeLoader())
            .catch(err => reject(err))
        } else {
          includes = config.includes.store.with_cities

          storeLoader()
        }
      })
    },

    loadCities({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        axios.get(`store/cities?with=${config.includes.cities}`)
          .then(({ data }) => {
            const cities = get(data, 'data.cities', [])
            const detectedCityId = get(data, 'data.detected_id')

            commit('setCities', cities)
            dispatch('determineActiveCity', detectedCityId)
            resolve(cities)
          })
          .catch(err => reject(err))
      })
    },

    determineActiveCity({ dispatch, state }, detectedCityId) {
      const savedCityId = storage.activeCityId()

      dispatch('saveActiveCity', savedCityId || detectedCityId || get(state, 'cities.0.id'))
    },

    saveActiveCity({ commit }, cityId) {
      commit('setActiveCity', cityId)
      storage.activeCityId(cityId)
    },

    saveStoreToStorage(context, store) {
      return new Promise(resolve => {
        storage.store(JSON.stringify(store))
        resolve(store)
      })
    },

    preloadStoreFromStorage({ commit }) {
      return new Promise(resolve => {
        let store = {}

        try {
          store = JSON.parse(storage.store())
        } catch (e) {
          store = {}
        }

        store = store || {}

        commit('setStore', store)
        resolve(store)
      })
    },

    loadBroadcastMessage({ commit }) {
      return new Promise(resolve => {
        axios.get(`store/broadcast-message?city_id=${storage.activeCityId()}`)
          .then(({ data }) => {
            commit('setBroadcastMessage', data.data)
            resolve(data.data)
          })
          .catch(err => {
            console.error('Unable to load broadcast message', { err })
          })
      })
    },

    showBroadcastMessage({ commit }, message) {
      commit('setBroadcastMessage', message);
    },
  },
}
